import { api } from '@/lib/api'

import { sourceLabels } from '@/shared/bql/api'
import { composeSearchBody } from '@/shared/opensearch/transforms/search-body'
import { resolveTimeRange } from '@/shared/opensearch/utils/time-preset'

import type {
	LogSchemasResponse,
	SavedSearch,
	SavedSearchResponse,
	SearchIntegrations,
	SearchParams,
} from './types'
import type { SearchLogResponse } from '@/shared/opensearch/types/response'
import type { StructuredFilter } from '@/shared/opensearch/utils/filter-to-clause'

/**
 * Search logs on OpenSearch.
 *
 * Composes the request body via `composeSearchBody` so timeframe always
 * lands in `bool.filter`, the user's outer combinator stays inside
 * `bool.must[0]`, and the histogram uses `auto_date_histogram` (50 buckets).
 *
 * @param searchParams - The URL-state search parameters
 * @param tenant - The tenant ID
 * @param page - 1-indexed page number
 * @returns The search results
 */
export const searchLogs = async (
	searchParams: SearchParams,
	tenant: string,
	page: number = 1,
): Promise<SearchLogResponse> => {
	// Return empty results if no integrations selected (valid state)
	if (!searchParams.int || searchParams.int.length === 0) {
		return {
			hits: { hits: [], max_score: 0, total: { value: 0, relation: 'eq' } },
			timed_out: false,
			took: 0,
		}
	}

	// Tenant and time range are required for actual search
	if (!tenant || !searchParams.tr) {
		throw new Error('Invalid search parameters: missing tenant or time range')
	}

	const resolvedTr = resolveTimeRange(searchParams.tr)
	if (!resolvedTr) {
		throw new Error('Invalid search parameters: unresolvable time range')
	}

	const body = {
		query: composeSearchBody({
			q: searchParams.q,
			tr: resolvedTr,
			ft: searchParams.ft as ReadonlyArray<StructuredFilter> | undefined,
			page,
		}),
		sources: searchParams.int.map((integration) => [
			integration.id,
			integration.ev,
		]),
	}

	// POST search: never pass `signal`. Cancelling a POST yields neither a
	// result nor an error, so an aborted page (e.g. on key change) would leave
	// the infinite query silently stuck. Let it resolve; stale pages are
	// discarded by the query key change.
	const { data } = await api.post<SearchLogResponse>(
		`/api/tenants/${tenant}/logs/search`,
		body,
	)

	return data
}

/**
 * The source and normalized schemas of every pair, in one request (the
 * sidebar field tree). Sent as repeated `source=<definition>:<event_type>`
 * through the BQL serializer, which the api client's flat `params` cannot
 * express. One pair's failure is its own `success: false` entry.
 */
export const getLogSchemas = async (
	tenant: string,
	pairs: SearchIntegrations,
	signal?: AbortSignal,
) => {
	const search = new URLSearchParams(
		sourceLabels(
			pairs.map((pair) => ({ definition: pair.id, event_type: pair.ev })),
		).map((label): [string, string] => ['source', label]),
	)
	const { data } = await api.get<LogSchemasResponse>(
		`/api/tenants/${tenant}/logs/schemas?${search}`,
		{ signal },
	)

	return data
}

/**
 * Get the saved searches
 * @param tenant - The tenant ID
 * @returns The saved searches
 */
export const getSavedSearches = async (
	tenant: string,
	signal?: AbortSignal,
) => {
	const { data } = await api.get<SavedSearchResponse>(
		`/api/tenants/${tenant}/logs/search/saved`,
		{
			signal,
		},
	)

	return data
}

/**
 * The path of one saved search, shared by load, save and delete.
 *
 * The name is free text — the form only checks its length — and `ApiClient`
 * resolves paths with `new URL`, which collapses `../` and treats `#` and `?`
 * as delimiters. Unencoded, a crafted name retargets the request, and one of
 * these three calls is a DELETE.
 */
const savedSearchPath = (tenant: string, name: string) =>
	`/api/tenants/${tenant}/logs/search/saved/${encodeURIComponent(name)}`

/**
 * Get a single saved search
 * @param tenant - The tenant ID
 * @param search_name - The search name
 * @returns The saved search
 */
export const getSingleSavedSearch = async (
	tenant: string,
	search_name: string,
	signal?: AbortSignal,
) => {
	const { data } = await api.get<{ saved_search: SavedSearch }>(
		savedSearchPath(tenant, search_name),
		{
			signal,
		},
	)

	return data
}

/**
 * Create a saved search
 * @param tenant - The tenant ID
 * @param search_name - The search name
 * @param search_string - The search string
 * @returns The saved search
 */
export const createSavedSearch = async (
	tenant: string,
	search_name: string,
	search_string: string,
) => {
	const { data } = await api.post<SavedSearch>(
		savedSearchPath(tenant, search_name),
		{
			search_string,
		},
	)

	return data
}

/**
 * Delete a saved search
 * @param tenant - The tenant ID
 * @param search_name - The search name
 * @returns The deleted search
 */
export const deleteSavedSearch = async (
	tenant: string,
	search_name: string,
) => {
	const { data } = await api.delete<string>(
		savedSearchPath(tenant, search_name),
	)

	return data
}
